/**
 * A track's speaker: one click mutes or unmutes the whole of it with its level kept, so nobody has to
 * drag a slider to the far left to silence a track (Dax, 2026-09-29). Used on every audio desk row and
 * on each clip's own sound.
 */
export function Speaker({ muted, label, onToggle, disabled, silenced }: {
  muted: boolean;
  /** What it silences, for the hover title: "the music", "this clip's own sound". */
  label: string;
  onToggle: () => void;
  disabled?: boolean;
  /** Silenced by another track's solo: shown muted and greyed, and left alone until the solo comes off. */
  silenced?: boolean;
}) {
  const title = silenced ? `Silenced by a solo: ${label}` : `${muted ? 'Unmute' : 'Mute'} ${label}`;
  return (
    <button type="button" className={`speaker${muted || silenced ? ' muted' : ''}${muted && !silenced ? ' on' : ''}`} title={title} aria-label={title} aria-pressed={muted || silenced} disabled={disabled || silenced} onClick={onToggle}>
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M2.5 6.5h2.5L8.5 3.5v9L5 9.5H2.5z" />
        {muted || silenced ? <path d="m11 6.5 3 3M14 6.5l-3 3" /> : <path d="M11 6a3 3 0 0 1 0 4M12.5 4.5a5 5 0 0 1 0 7" />}
      </svg>
    </button>
  );
}

/**
 * A track's solo (Dax, 2026-09-30): lit while on. While any track is soloed only the soloed ones are
 * heard; a second solo joins the first, and each comes off with its own button.
 */
export function Solo({ on, label, onToggle, disabled }: {
  on: boolean;
  label: string;
  onToggle: () => void;
  disabled?: boolean;
}) {
  const title = `${on ? 'Unsolo' : 'Solo'} ${label}`;
  return (
    <button type="button" className={`solo${on ? ' on' : ''}`} title={title} aria-label={title} aria-pressed={on} disabled={disabled} onClick={onToggle}>
      S
    </button>
  );
}
